import { config } from '../config';
import { log, setCorrelationId } from '../app/logger';
import { MeasurementError, errorMessage } from '../app/errors';
import {
  concatChunks,
  deleteSegment,
  getSegment,
  listChunks,
  listIncompleteSegments,
  listSegments,
  putSegment,
  trimSegments,
} from '../data/audioSpikeStore';
import type { AudioSegmentRow } from '../data/harnessDb';
import { captureEnvironment } from './environment';
import { probeStorage } from './storageProbe';
import { indicesForVariant } from './audioScenario';
import { browserProbeDeps, probeBlob } from './playability';
import type { ProbeDeps } from './playability';
import { MicrophoneStream, SpikeRecording } from './audioRecorder';
import type { SegmentOutcome } from './audioRecorder';
import { browserAudioPlatform } from './audioPlatform';
import type { AudioPlatform } from './audioPlatform';
import { probeCapabilities, probeDefaultMime } from './audioCapabilities';
import { saveAudioRun } from './audioResultsStore';
import type {
  AssemblyVariant,
  AudioCapabilityReport,
  AudioRunRecord,
  FinalizeTrigger,
  GumTrial,
  ManualVerdict,
  PlayabilityResult,
  SegmentRecord,
} from '../types';

/**
 * Composes one U-1 spike: record → persist chunks as they arrive → finalize → assemble verbatim →
 * probe → persist → check the recorder still works.
 *
 * Kept separate from `audioRecorder.ts` so the recorder stays about the AUD-3 contract and this
 * file stays about the experiment. Everything here happens strictly outside a recording.
 *
 * ── The four assemblies, and why each is probed ──────────────────────────────
 * See `indicesForVariant` in `audioScenario.ts`. In short: `full` is A-2's claim, `truncatedTail`
 * is design §8.5's hard-jetsam case, `firstChunkOnly` is the shortest flag the product can produce,
 * and `withoutFirstChunk` is the negative control that decides whether "init fragment first" is a
 * real constraint on this browser or a precaution.
 */

const MODULE = 'audio-session';

const VARIANTS: readonly AssemblyVariant[] = [
  'full',
  'truncatedTail',
  'firstChunkOnly',
  'withoutFirstChunk',
];

export interface SpikeContext {
  platform: AudioPlatform;
  mic: MicrophoneStream;
  probeDeps: ProbeDeps | null;
}

/**
 * @param probeDeps `null` when the environment has no `Audio` constructor. Resolved lazily so a
 *   browser without one produces probe results carrying an `error` rather than an exception —
 *   "we could not ask" and "the answer is no" must not read the same.
 */
export function createSpikeContext(platform: AudioPlatform = browserAudioPlatform()): SpikeContext {
  let probeDeps: ProbeDeps | null = null;
  try {
    probeDeps = browserProbeDeps();
  } catch (cause) {
    log.warn(MODULE, 'no playability probe available in this environment', {
      error: errorMessage(cause),
    });
  }
  return { platform, mic: new MicrophoneStream(platform), probeDeps };
}

function newSegmentId(): string {
  return `u1-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// One segment
// ─────────────────────────────────────────────────────────────────────────────

export interface BeginSegmentOptions {
  label: string;
  /**
   * SES-10 — the minute is derived at the moment recording STARTS, never at finalize. The spike has
   * no state machine, so the caller supplies the value; capturing it here at start is what
   * demonstrates the ordering the requirement turns on (a flag begun at 44′ and finalized by the
   * Half tap is a 44′ flag).
   */
  matchMinuteAtStart: number;
  plannedSeconds: number;
  requestedMime: string | null;
  /** AUD-3: attempt a fresh recording immediately after this one finalizes. */
  verifyRecorderReuse: boolean;
  onChunk?: (chunkCount: number, totalBytes: number) => void;
  onPhase?: (phase: string) => void;
}

export interface ActiveSegment {
  id: string;
  /** Stop early. Any trigger; a real interruption calls `finalize` itself and this becomes a no-op. */
  stop: (trigger: FinalizeTrigger) => void;
  chunkCount: () => number;
  elapsedMs: () => number;
  /** Resolves once the segment has been finalized, assembled, probed and persisted. */
  done: Promise<SegmentRecord>;
}

/**
 * Starts a segment and returns immediately, so the UI stays live while recording.
 *
 * The returned `done` resolves however the recording ends — the user's tap, the planned duration,
 * `VOICE_NOTE_MAX_S`, a phone call, a page hide. That is the point: AUD-3 says every one of those
 * routes through one finalize, so the caller must not have to know which happened.
 *
 * @throws {MeasurementError} when the microphone cannot be acquired or the recorder will not start.
 *   Surfaced to the screen; the spike has no FLAG-4 silent path (that is U-11's, and inventing one
 *   here would hide the very failure this run is measuring).
 */
export async function beginSegment(
  ctx: SpikeContext,
  options: BeginSegmentOptions,
): Promise<ActiveSegment> {
  const id = newSegmentId();
  setCorrelationId(id);
  options.onPhase?.('Acquiring microphone');

  const acquired = await ctx.mic.acquire();

  const recording = new SpikeRecording(ctx.platform, acquired.stream, {
    segmentId: id,
    requestedMime: options.requestedMime,
    plannedSeconds: options.plannedSeconds,
    anchorMs: ctx.platform.now(),
    onChunk: () => options.onChunk?.(recording.chunkCount(), 0),
  });

  const startedAt = Date.now();
  await recording.start();
  options.onPhase?.('Recording');

  // Written before any audio is finalized, so a hard kill during the recording still leaves a row
  // for the §8.5 recovery pass to find. Mirrors design §8.2's "the Observation is written at
  // recording START" — the row exists first, the bytes land under it later.
  await putSegment({
    id,
    startedAt,
    complete: false,
    mime: '',
    chunkCount: 0,
    sizeBytes: 0,
    blob: null,
    payload: null,
  });

  const done = (async (): Promise<SegmentRecord> => {
    const outcome = await recording.finalized;
    options.onPhase?.('Assembling');
    const record = await completeSegment(ctx, id, startedAt, options, outcome);
    options.onPhase?.('Done');
    setCorrelationId(null);
    return record;
  })();

  return {
    id,
    stop: (trigger) => void recording.finalize(trigger),
    chunkCount: () => recording.chunkCount(),
    elapsedMs: () => recording.elapsedMs(),
    done,
  };
}

/**
 * Assembles, probes, persists, and checks the recorder survived.
 *
 * The assembly is `concatChunks` and nothing else — AUD-1 forbids transcoding on any path, so if a
 * fragmented-MP4 sequence needs fixing up to play, that is A-2 being false, not a step to add.
 */
async function completeSegment(
  ctx: SpikeContext,
  id: string,
  startedAt: number,
  options: BeginSegmentOptions,
  outcome: SegmentOutcome,
): Promise<SegmentRecord> {
  const chunks = await listChunks(id);
  const mime = outcome.actualMime ?? '';
  const probes = await probeAllVariants(ctx, chunks, mime, options.onPhase);

  const full = concatChunks(chunks, indicesForVariant(chunks.length, 'full'), mime);

  const record: SegmentRecord = {
    id,
    startedAt,
    label: options.label,
    matchMinuteAtStart: options.matchMinuteAtStart,
    trigger: outcome.trigger,
    requestedMime: options.requestedMime,
    actualMime: outcome.actualMime,
    chunks: outcome.chunks,
    totalBytes: full.sizeBytes,
    wallDurationMs: outcome.wallDurationMs,
    complete: true,
    probes,
    recorderReusable: { attempted: false, succeeded: false, message: null },
    manualVerdict: 'unset',
    notes:
      outcome.chunks.length !== chunks.length
        ? `${outcome.chunks.length} chunk(s) arrived but ${chunks.length} were readable from storage — the difference is chunks that failed to persist.`
        : null,
  };

  if (options.verifyRecorderReuse) {
    options.onPhase?.('Checking the recorder still works');
    record.recorderReusable = await verifyRecorderReuse(ctx);
  }

  await putSegment({
    id,
    startedAt,
    complete: true,
    mime,
    chunkCount: full.chunkCount,
    sizeBytes: full.sizeBytes,
    blob: full.blob,
    payload: record,
  });

  const swept = await trimSegments();
  if (swept.length > 0) log.info(MODULE, 'swept old segments', { ids: swept });

  return record;
}

async function probeAllVariants(
  ctx: SpikeContext,
  chunks: Awaited<ReturnType<typeof listChunks>>,
  mime: string,
  onPhase?: (phase: string) => void,
): Promise<PlayabilityResult[]> {
  const results: PlayabilityResult[] = [];

  for (const variant of VARIANTS) {
    const indices = indicesForVariant(chunks.length, variant);
    // A variant that cannot be formed from this many chunks is skipped, not probed as an empty
    // blob — an unplayable zero-byte file would read as a container failure it is not.
    if (indices.length === 0) continue;

    onPhase?.(`Probing ${variant}`);
    const assembled = concatChunks(chunks, indices, mime);

    if (!ctx.probeDeps) {
      results.push({
        variant,
        chunkCount: assembled.chunkCount,
        sizeBytes: assembled.sizeBytes,
        mime,
        canPlayThrough: false,
        metadataLoaded: false,
        durationSeconds: null,
        mediaErrorCode: null,
        mediaErrorMessage: null,
        probeMs: 0,
        timedOut: false,
        error: 'No Audio constructor in this environment — the probe could not run.',
      });
      continue;
    }

    results.push(
      await probeBlob(
        { blob: assembled.blob, variant, chunkCount: assembled.chunkCount, mime },
        ctx.probeDeps,
      ),
    );
  }

  return results;
}

/**
 * AUD-3: "the recorder is never left in a state where a subsequent record attempt fails."
 *
 * Constructs a fresh recorder on the held stream, starts it, and finalizes it immediately. Nothing
 * is persisted — the question is whether the attempt succeeds at all. A stream whose tracks ended
 * (a phone call) is re-acquired first, which is §8.1's `readyState` guard doing its job and is part
 * of what "the next attempt succeeds" has to mean.
 */
async function verifyRecorderReuse(
  ctx: SpikeContext,
): Promise<{ attempted: boolean; succeeded: boolean; message: string | null }> {
  try {
    const acquired = await ctx.mic.acquire();
    const probe = new SpikeRecording(ctx.platform, acquired.stream, {
      segmentId: `reuse-${Date.now().toString(36)}`,
      requestedMime: null,
      plannedSeconds: config.AUDIO_SPIKE_SEGMENT_MIN_S,
      anchorMs: ctx.platform.now(),
      persistChunks: false,
    });
    await probe.start();
    const outcome = await probe.finalize('userStop');
    return {
      attempted: true,
      succeeded: true,
      message: `Restarted successfully${acquired.reusedHeldStream ? ' on the held stream' : ' after re-acquiring the stream'} (mime ${outcome.actualMime ?? '—'}).`,
    };
  } catch (cause) {
    log.error(MODULE, 'recorder could not be re-used after finalize — AUD-3 violated', {
      error: errorMessage(cause),
    });
    return { attempted: true, succeeded: false, message: errorMessage(cause) };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Recovery (design §8.5)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Design §8.5: *"Recovery runs on `pageshow`/resume, not `pagehide`: any `AudioBlob` with
 * `complete=false` is assembled and probed at launch. Worst-case loss is one timeslice, not the
 * whole segment."*
 *
 * This is what proves the claim rather than asserting it. Kill the app mid-recording — swipe it out
 * of the app switcher, which gives no `pagehide` — relaunch, and this pass reports exactly how much
 * of the recording survived and whether it plays.
 */
export async function recoverIncompleteSegments(ctx: SpikeContext): Promise<SegmentRecord[]> {
  const rows = await listIncompleteSegments();
  if (rows.length === 0) return [];

  log.warn(MODULE, 'found incomplete segments from a previous launch', { count: rows.length });
  const recovered: SegmentRecord[] = [];

  for (const row of rows) {
    const chunks = await listChunks(row.id);
    if (chunks.length === 0) {
      // A row with no chunks is a recording that died before its first timeslice. There is nothing
      // to recover and nothing to learn from keeping it, so it is removed rather than reported
      // forever as an unrecoverable segment.
      await deleteSegment(row.id);
      log.info(MODULE, 'discarded an empty incomplete segment', { id: row.id });
      continue;
    }

    const mime = chunks[0]?.type ?? '';
    const probes = await probeAllVariants(ctx, chunks, mime);
    const full = concatChunks(chunks, indicesForVariant(chunks.length, 'full'), mime);

    const record: SegmentRecord = {
      id: row.id,
      startedAt: row.startedAt,
      label: 'recovered after a hard kill',
      matchMinuteAtStart: -1,
      trigger: 'recovered',
      requestedMime: null,
      actualMime: mime === '' ? null : mime,
      chunks: chunks.map((c, i) => ({
        index: c.index,
        sinceStartMs: Number.NaN,
        sinceLastMs: i === 0 ? null : Number.NaN,
        sizeBytes: c.sizeBytes,
        type: c.type,
        persistMs: Number.NaN,
        persistError: null,
      })),
      totalBytes: full.sizeBytes,
      wallDurationMs: Number.NaN,
      complete: false,
      probes,
      recorderReusable: { attempted: false, succeeded: false, message: null },
      manualVerdict: 'unset',
      notes:
        'Recovered on launch from chunks that survived a kill with no pagehide (design §8.5). Timing fields are unavailable — the process that measured them is gone. This segment is reported but is excluded from the A-2 verdict, which scores complete segments only.',
    };

    await putSegment({
      ...row,
      complete: false,
      mime,
      chunkCount: full.chunkCount,
      sizeBytes: full.sizeBytes,
      blob: full.blob,
      payload: record,
    });

    recovered.push(record);
  }

  return recovered;
}

// ─────────────────────────────────────────────────────────────────────────────
// Reading segments back
// ─────────────────────────────────────────────────────────────────────────────

/** Every persisted segment's record, newest first. @throws {StorageError} */
export async function loadSegmentRecords(): Promise<SegmentRecord[]> {
  const rows = await listSegments();
  return rows
    .map((r) => r.payload as SegmentRecord | null)
    .filter((r): r is SegmentRecord => r !== null);
}

/**
 * The assembled blob for a variant, for the Play control.
 *
 * Re-assembled from chunks rather than served from the stored `blob`, so what Andrew hears is
 * produced by the same `concatChunks` the probe scored — if the two differed, the ear would be
 * checking something the verdict never looked at.
 */
export async function assembleForPlayback(
  segmentId: string,
  variant: AssemblyVariant,
): Promise<{ blob: Blob; mime: string } | null> {
  const row = await getSegment(segmentId);
  if (!row) return null;
  const chunks = await listChunks(segmentId);
  const indices = indicesForVariant(chunks.length, variant);
  if (indices.length === 0) return null;
  const { blob } = concatChunks(chunks, indices, row.mime);
  return { blob, mime: row.mime };
}

/**
 * Records what Andrew heard. Persisted, because AUD-1's contract is playback and the probe can be
 * wrong in both directions — this is the only signal in the run that comes from a human ear.
 *
 * @throws {MeasurementError} when the segment is not on disk.
 */
export async function setManualVerdict(segmentId: string, verdict: ManualVerdict): Promise<void> {
  const row = await getSegment(segmentId);
  if (!row) throw new MeasurementError(`Segment ${segmentId} is not in storage.`);
  const payload = row.payload as SegmentRecord | null;
  if (!payload) throw new MeasurementError(`Segment ${segmentId} has no record to annotate.`);
  await putSegment({ ...row, payload: { ...payload, manualVerdict: verdict } });
  log.info(MODULE, 'manual playback verdict recorded', { segmentId, verdict });
}

// ─────────────────────────────────────────────────────────────────────────────
// The run record
// ─────────────────────────────────────────────────────────────────────────────

export interface BuildRunOptions {
  capabilities: AudioCapabilityReport;
  gumTrials: GumTrial[];
  segments: SegmentRecord[];
  failures: { stage: string; message: string }[];
  requestPersist: boolean;
  notes?: string | null;
}

/**
 * Assembles and persists the run.
 *
 * Returns the record even when persistence fails, with the failure written into `notes`, so a
 * measurement taken once at a venue can still be exported from the screen. Losing it silently is
 * the worst outcome this harness has.
 */
export async function buildAndSaveRun(options: BuildRunOptions): Promise<AudioRunRecord> {
  const storage = await probeStorage(options.requestPersist);
  const startedAt = options.segments[options.segments.length - 1]?.startedAt ?? Date.now();

  const record: AudioRunRecord = {
    id: `u1-run-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    startedAt,
    finishedAt: Date.now(),
    capabilities: options.capabilities,
    gumTrials: options.gumTrials,
    segments: options.segments,
    environment: captureEnvironment(),
    storage,
    failures: options.failures,
    notes: options.notes ?? null,
  };

  try {
    await saveAudioRun(record);
  } catch (cause) {
    log.error(MODULE, 'spike run could not be persisted; it exists only in memory', {
      error: errorMessage(cause),
    });
    return {
      ...record,
      notes: `NOT SAVED TO DISK: ${errorMessage(cause)} — export before leaving this screen.`,
    };
  }
  return record;
}

/** Fills in the capability report's `defaultMime`, which needs a real stream. */
export async function completeCapabilities(
  ctx: SpikeContext,
  report: AudioCapabilityReport,
): Promise<AudioCapabilityReport> {
  const stream = ctx.mic.current();
  if (!stream) return report;
  return { ...report, defaultMime: probeDefaultMime(ctx.platform, stream) };
}

export { probeCapabilities };
export type { AudioSegmentRow };
